/**
 * PURPOSE: Punto de entrada del panel administrativo de Comunicados (rol 1), montado en la pestaña
 *   "Comunicados" de AdminSection.jsx (FTR-0005 Phase 4, precedente FTR-0001 "Oficiales").
 * RESPONSIBILITIES: Orquestar historial + composición: cargar/paginar el historial, seleccionar un
 *   ítem (crear nuevo / editar borrador / ver publicado), y coordinar los modales de
 *   publicación/ocultamiento. Deliberadamente separado de AuditNotificationPanel/
 *   auditNotificationService (auditoría interna, "Actividad reciente") — dominios no relacionados.
 * COLLABORATORS: HayllisCommunicationsList.jsx, HayllisCommunicationForm.jsx,
 *   HayllisCommunicationDetailReadOnly.jsx, HayllisPublishConfirmModal.jsx,
 *   hayllisNotificationsService.js, AdminSection.jsx.
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisCommunicationsPanel.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  getApiErrorMessage,
  getHayllisCommunicationDetail,
  hideHayllisCommunication,
  listHayllisCommunications,
} from "../../services/hayllisNotificationsService.js";
import HayllisCommunicationsList from "./HayllisCommunicationsList.jsx";
import HayllisCommunicationForm from "./HayllisCommunicationForm.jsx";
import HayllisCommunicationDetailReadOnly from "./HayllisCommunicationDetailReadOnly.jsx";
import HayllisPublishConfirmModal from "./HayllisPublishConfirmModal.jsx";

const PAGE_LIMIT = 20;

const HayllisCommunicationsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  // selected === null → composer in "create" mode. Otherwise the full admin notification object
  // (list and detail share the same mapper shape, so a list row can be used directly).
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const payload = await listHayllisCommunications({ limit: PAGE_LIMIT, status: statusFilter || undefined });
      setItems(payload.items);
      setHasMore(payload.hasMore);
      setNextCursor(payload.nextCursor);
    } catch (error) {
      console.error("Error al cargar el historial de comunicados", error);
      setLoadError(getApiErrorMessage(error, "No se pudo cargar el historial de comunicados."));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const payload = await listHayllisCommunications({
        limit: PAGE_LIMIT,
        status: statusFilter || undefined,
        beforeId: nextCursor,
      });
      setItems((current) => [...current, ...payload.items]);
      setHasMore(payload.hasMore);
      setNextCursor(payload.nextCursor);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudieron cargar más comunicados."));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreateNew = () => setSelected(null);

  const handleSelect = async (item) => {
    setDetailLoading(true);
    try {
      // Re-fetch fresh detail: push counters/status can change asynchronously (receipts arrive up
      // to ~20 min after send), so the cached list row may be stale by the time it's opened.
      const fresh = await getHayllisCommunicationDetail(item.id);
      setSelected(fresh || item);
    } catch (error) {
      console.error("No se pudo refrescar el detalle, se usa la fila del historial", error);
      setSelected(item);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaved = (notification) => {
    setSelected(notification);
    loadFirstPage();
  };

  const handleRequestPublish = (notification) => setPublishTarget(notification);
  const handlePublishCancel = () => setPublishTarget(null);
  const handlePublished = (notification) => {
    setPublishTarget(null);
    setSelected(notification);
    loadFirstPage();
  };

  const handleRequestHide = async (notification) => {
    const message =
      notification.status === "PUBLISHED"
        ? "Este comunicado dejará de aparecer en la bandeja de Hayllis Mobile. El push ya enviado no se puede recuperar de los dispositivos que lo recibieron. ¿Deseas ocultarlo?"
        : "¿Deseas descartar este borrador? Podrás seguir viéndolo en el historial, marcado como oculto.";
    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;

    try {
      const hidden = await hideHayllisCommunication(notification.id);
      toast.success("Comunicado ocultado.");
      setSelected(hidden);
      loadFirstPage();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo ocultar el comunicado."));
    }
  };

  const mode = selected?.status === "DRAFT" ? "edit" : selected ? "view" : "create";

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(360px,1fr)_minmax(0,1.3fr)]">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {mode === "create" ? "Nuevo comunicado" : mode === "edit" ? "Editar borrador" : "Comunicado publicado"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              {mode === "view"
                ? "El contenido publicado no puede editarse."
                : "Título, mensaje y adjunto opcional. Audiencia: Todos."}
            </p>
          </div>
          {selected && (
            <button
              type="button"
              onClick={handleCreateNew}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800"
            >
              Nuevo
            </button>
          )}
        </div>

        {detailLoading ? (
          <div className="py-10 text-center text-sm text-slate-500 dark:text-slate-300">Cargando comunicado...</div>
        ) : mode === "view" ? (
          <HayllisCommunicationDetailReadOnly notification={selected} onRequestHide={handleRequestHide} />
        ) : (
          <HayllisCommunicationForm
            mode={mode}
            initialData={selected}
            onSaved={handleSaved}
            onRequestPublish={handleRequestPublish}
            onRequestHide={handleRequestHide}
          />
        )}
      </section>

      <HayllisCommunicationsList
        items={items}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        loadError={loadError}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onLoadMore={handleLoadMore}
        onSelect={handleSelect}
        onCreateNew={handleCreateNew}
        selectedId={selected?.id ?? null}
      />

      <HayllisPublishConfirmModal
        notification={publishTarget}
        onCancel={handlePublishCancel}
        onPublished={handlePublished}
      />
    </div>
  );
};

export default HayllisCommunicationsPanel;
